import type { ResourceItem } from "@rago/web-shared";
import { ApiProblemError } from "../lib/authManager";
import { normalizeAnalysis } from "../lib/analysis";
import { getCapabilityProfile } from "../lib/capabilityProfile";
import { partIdFromPath } from "../lib/mediaFormat";
import { ServerConnection, mintAssetToken } from "../lib/serverConnection";
import { ServerWatchState } from "./serverWatchState";
import type {
  Hub,
  Library,
  LibraryItemsQuery,
  LibraryPage,
  LibrarySettings,
  MediaInfo,
  MediaItem,
  PartAnalysis,
  PlaybackInfo,
  PlaybackLimit,
  PlaybackOptions,
  PlaybackVerdict,
  RagoSource,
  StreamInfo,
  TimelineExtras,
  TranscodeTarget,
  UserItemState,
  LetterBucket,
  LibraryFacets,
  LibraryFilterQuery,
} from "./types";
import { libraryFilterSearchParams, libraryItemsSearchParams } from "./libraryQuery";

/** A 404 from the server: the route does not exist there (feature-detect). */
function isNotFound(err: unknown): boolean {
  const e = err as { status?: unknown; problem?: { status?: unknown } } | null;
  return e?.status === 404 || e?.problem?.status === 404;
}

/**
 * ServerSource implements RagoSource over the rago-server §4c API. Item
 * shapes mirror §4c JSON so the UI never changes. userState travels on every
 * payload and is cached (sync getUserState); artwork and files carry a type-5
 * asset token in the query string because <img>/<video> can't send headers.
 */

interface ServerStream {
  index: number;
  type: string;
  codec?: string;
  language?: string;
  channels?: number;
  width?: number;
  height?: number;
  bitrate?: number;
  frameRate?: number;
  default?: boolean;
  forced?: boolean;
  hearingImpaired?: boolean;
  external?: boolean;
  /** Managed-download provenance ("opensubtitles"); "" for probed/sidecar. */
  source?: string;
  title?: string;
  profile?: string;
  level?: number;
  bitDepth?: number;
  colorTransfer?: string;
  colorPrimaries?: string;
  colorSpace?: string;
  colorRange?: string;
  pixelFormat?: string;
  codecTag?: string;
  chromaLocation?: string;
  codedWidth?: number;
  codedHeight?: number;
  refFrames?: number;
  fieldOrder?: string;
  sampleAspectRatio?: string;
  sampleRate?: number;
  channelLayout?: string;
  videoRange?: string;
  hdr10Plus?: boolean;
  dolbyVision?: {
    profile: number;
    level: number;
    blCompatId: number;
    rpuPresent: boolean;
    elPresent: boolean;
    blPresent: boolean;
  };
}

interface ServerPart {
  id: string;
  /** Base name of the file — absent on older servers. */
  fileName?: string;
  /** Absolute path — the owner's item detail only. */
  path?: string;
  sizeBytes: number;
  durationMs?: number;
  container?: string;
  streams: ServerStream[];
}

interface ServerMedia {
  id: string;
  durationMs?: number;
  container?: string;
  videoCodec?: string;
  audioCodec?: string;
  /** bits per second (ffprobe format bit_rate). */
  bitrate?: number;
  width?: number;
  height?: number;
  parts: ServerPart[];
}

interface ServerItem {
  id: string;
  guid: string;
  /** Batch lookups only — the canonical guid the copy was asked under. */
  lookupGuid?: string;
  type: "movie" | "show" | "season" | "episode";
  title: string;
  sortTitle: string;
  year: number | null;
  summary: string;
  tagline?: string;
  contentRating?: string;
  genres: string[];
  runtimeMs: number;
  studio?: string;
  releaseDate?: string | null;
  rating?: number | null;
  ratings?: Array<{ provider: string; value: number; votes?: number }>;
  externalIds: Array<{ provider: string; id: string }>;
  addedAt: string;
  updatedAt?: string;
  images: { poster: boolean; backdrop: boolean; thumb?: boolean };
  media: ServerMedia[];
  index?: number | null;
  parentId?: string | null;
  parentIndex?: number | null;
  grandparentId?: string | null;
  grandparentTitle?: string | null;
  /** Show poster presence on episodes; older servers omit it. */
  grandparentImages?: { poster: boolean } | null;
  /** On Deck marker on Continue Watching / next-up rows; older servers omit it. */
  onDeck?: { playedAt: string | null; resume: boolean } | null;
  airDate?: string | null;
  childCount?: number | null;
  userState: {
    watched: boolean;
    viewOffsetMs: number;
    viewCount: number;
    rating: number | null;
    lastPlayedAt: string | null;
    audioStreamIndex?: number | null;
    subtitleStreamIndex?: number | null;
  };
}

interface ServerLibrary {
  id: string;
  type?: "movie" | "show";
  title: string;
  agent?: "tmdb" | "tvdb";
  providers?: Array<"tmdb" | "tvdb">;
  language?: string;
  itemCount?: number;
  unmatchedCount?: number;
  locations?: string[];
  settings?: LibrarySettings;
}

interface Envelope<T> {
  meta: { offset: number; limit: number; total: number };
  items: T[];
}

/** Physical info of one media version (the first drives the quality menu). */
function toMediaInfo(m: ServerMedia): MediaInfo {
  return {
    mediaId: m.id ?? null,
    container: m.container ?? null,
    videoCodec: m.videoCodec ?? null,
    audioCodec: m.audioCodec ?? null,
    bitrateKbps: m.bitrate ? Math.round(m.bitrate / 1000) : null,
    width: m.width ?? null,
    height: m.height ?? null,
    sizeBytes: m.parts?.reduce((n, p) => n + (p.sizeBytes || 0), 0) || null,
    // The first part is the one the detail UI (and the server's part
    // resolution) treats as the item's file.
    partId: m.parts?.[0]?.id ?? null,
    fileName: m.parts?.[0]?.fileName ?? null,
    filePath: m.parts?.[0]?.path ?? null,
    durationMs: m.durationMs ?? m.parts?.[0]?.durationMs ?? null,
    streams: toStreamList(m.parts?.[0]?.streams),
  };
}

/** First part's probed streams (the track menus' data). */
function toStreams(media: ServerMedia[] | undefined): StreamInfo[] | undefined {
  return toStreamList(media?.[0]?.parts?.[0]?.streams);
}

function toStreamList(streams: ServerStream[] | undefined): StreamInfo[] | undefined {
  if (!streams) return undefined;
  return streams
    .filter((st): st is ServerStream & { type: StreamInfo["type"] } =>
      st.type === "video" || st.type === "audio" || st.type === "subtitle")
    .map((st) => ({
      index: st.index,
      type: st.type,
      codec: st.codec ?? "",
      language: st.language ?? "",
      channels: st.channels ?? 0,
      width: st.width ?? 0,
      height: st.height ?? 0,
      bitrate: st.bitrate ?? 0,
      frameRate: st.frameRate ?? 0,
      default: st.default ?? false,
      forced: st.forced ?? false,
      hearingImpaired: st.hearingImpaired ?? false,
      external: st.external ?? false,
      source: st.source || undefined,
      title: st.title ?? "",
      profile: st.profile || undefined,
      level: st.level || undefined,
      bitDepth: st.bitDepth || undefined,
      colorTransfer: st.colorTransfer || undefined,
      colorPrimaries: st.colorPrimaries || undefined,
      colorSpace: st.colorSpace || undefined,
      colorRange: st.colorRange || undefined,
      pixelFormat: st.pixelFormat || undefined,
      codecTag: st.codecTag || undefined,
      chromaLocation: st.chromaLocation || undefined,
      codedWidth: st.codedWidth || undefined,
      codedHeight: st.codedHeight || undefined,
      refFrames: st.refFrames || undefined,
      fieldOrder: st.fieldOrder || undefined,
      sampleAspectRatio: st.sampleAspectRatio || undefined,
      sampleRate: st.sampleRate || undefined,
      channelLayout: st.channelLayout || undefined,
      videoRange: st.videoRange || undefined,
      hdr10Plus: st.hdr10Plus || undefined,
      dolbyVision: st.dolbyVision ?? undefined,
    }));
}

export class ServerSource implements RagoSource {
  readonly id: string;
  readonly kind = "server" as const;
  // Mutable: renames land via /v1/resources refetches, and registry
  // instances outlive them — a frozen name would pin the chip/nav labels
  // to whatever the first fetch said.
  name: string;
  readonly serverHash: string;
  readonly conn: ServerConnection;
  readonly watch = new ServerWatchState();

  private assetToken = "";
  private assetTokenExpiresAt = 0;
  private assetTokenInflight: Promise<string> | null = null;

  constructor(resource: ResourceItem) {
    this.id = resource.serverId;
    this.name = resource.name;
    this.serverHash = resource.serverHash;
    this.conn = new ServerConnection(resource);
  }

  update(resource: ResourceItem): void {
    this.name = resource.name;
    this.conn.update(resource);
  }

  private ingestState(itemId: string, s: ServerItem["userState"]): UserItemState {
    const state: UserItemState = {
      watched: s.watched,
      viewOffsetMs: s.viewOffsetMs,
      viewCount: s.viewCount,
      lastPlayedAt: s.lastPlayedAt ? Date.parse(s.lastPlayedAt) : null,
      audioStreamIndex: s.audioStreamIndex ?? null,
      subtitleStreamIndex: s.subtitleStreamIndex ?? null,
    };
    this.watch.ingest(itemId, state);
    return state;
  }

  private toMediaItem(it: ServerItem): MediaItem {
    this.ingestState(it.id, it.userState);
    const versions = (it.media ?? []).map(toMediaInfo);
    return {
      id: it.id,
      sourceId: this.id,
      guid: it.guid,
      type: it.type,
      title: it.title,
      sortTitle: it.sortTitle,
      year: it.year ?? 0,
      summary: it.summary,
      tagline: it.tagline || undefined,
      contentRating: it.contentRating || undefined,
      genres: it.genres ?? [],
      runtimeMs: it.runtimeMs,
      studio: it.studio || undefined,
      releaseDate: it.releaseDate ?? null,
      rating: it.rating ?? null,
      ratings: it.ratings ?? [],
      externalIds: it.externalIds ?? [],
      addedAt: it.addedAt,
      updatedAt: it.updatedAt,
      // Presence sentinels: imageUrl only checks length, then builds the
      // real URL from the item id + connection + asset token.
      images: {
        poster: it.images.poster ? [{ width: 342, url: `__poster__:${it.id}` }] : [],
        backdrop: it.images.backdrop ? [{ width: 1280, url: `__backdrop__:${it.id}` }] : [],
        thumb: it.images.thumb ? [{ width: 576, url: `__thumb__:${it.id}` }] : [],
      },
      seasonCount: it.type === "show" ? (it.childCount ?? null) : null,
      index: it.index ?? null,
      airDate: it.airDate ?? null,
      childCount: it.childCount ?? null,
      parentId: it.parentId ?? null,
      mediaInfo: versions[0] ?? null,
      versions,
      streams: toStreams(it.media),
      episode:
        it.type === "episode"
          ? {
              index: it.index ?? null,
              parentId: it.parentId ?? null,
              parentIndex: it.parentIndex ?? null,
              grandparentId: it.grandparentId ?? null,
              grandparentTitle: it.grandparentTitle ?? null,
              grandparentImages: it.grandparentImages ?? undefined,
            }
          : null,
      onDeck: it.onDeck
        ? { playedAt: it.onDeck.playedAt ? Date.parse(it.onDeck.playedAt) : null, resume: it.onDeck.resume }
        : undefined,
    };
  }

  private assetTokenFresh(): boolean {
    return Boolean(this.assetToken) && Date.now() < this.assetTokenExpiresAt - 60_000;
  }

  private async ensureAssetToken(): Promise<string> {
    if (this.assetTokenFresh()) return this.assetToken;
    // Single-flight: a grid of posters must not trigger a mint storm.
    if (!this.assetTokenInflight) {
      this.assetTokenInflight = (async () => {
        try {
          const t = await mintAssetToken(this.conn);
          this.assetToken = t.token;
          this.assetTokenExpiresAt = t.expiresAt;
          // Minting went through conn.request, so the base URL is established
          // too — tick subscribers so <img> srcs rebuild with both in hand.
          this.conn.notifyAssets();
          return this.assetToken;
        } finally {
          this.assetTokenInflight = null;
        }
      })();
    }
    return this.assetTokenInflight;
  }

  async getLibraries(): Promise<Library[]> {
    const res = await this.conn.request<Envelope<ServerLibrary>>("/v1/libraries");
    return res.items.map((l) => ({
      id: l.id,
      sourceId: this.id,
      type: l.type ?? "movie",
      title: l.title,
      agent: l.agent,
      providers: l.providers,
      language: l.language,
      itemCount: l.itemCount,
      unmatchedCount: l.unmatchedCount,
      locations: l.locations,
      settings: l.settings,
    }));
  }

  /** A show's seasons or a season's episodes, as full items. User state
   * rides along (toMediaItem ingests it), so watched dots light up without
   * a second request. Leaves (movies, episodes) yield an empty list. */
  async getChildren(itemId: string): Promise<MediaItem[]> {
    await this.ensureAssetToken();
    const res = await this.conn.request<Envelope<ServerItem>>(
      `/v1/items/${itemId}/children?limit=200`,
    );
    return res.items.map((it) => this.toMediaItem(it));
  }

  /**
   * GET /v1/items/{id}/next-up — the show/season page's Play target by the
   * server's Continue Watching rule, so the button and the rail never
   * disagree. A 204 (nothing unwatched after the last play) is null; a server
   * predating the route answers a 404 problem → undefined (feature-detect,
   * never version-sniff) and the caller walks the hierarchy itself.
   */
  async getNextUp(rootId: string): Promise<MediaItem | null | undefined> {
    await this.ensureAssetToken();
    try {
      const it = await this.conn.request<ServerItem | undefined>(`/v1/items/${rootId}/next-up`);
      return it ? this.toMediaItem(it) : null;
    } catch (err) {
      if (err instanceof ApiProblemError && err.problem.status === 404) return undefined;
      throw err;
    }
  }

  /** Artwork URL for a hierarchy child (season poster, episode still). */
  childImageUrl(childId: string, kind: "poster" | "thumb", width: number): string {
    const base = this.conn.baseUrlSync();
    if (!base || !this.assetTokenFresh()) {
      void this.ensureAssetToken().catch(() => {});
      if (!base || !this.assetToken) return "";
    }
    return `${base}/v1/items/${childId}/images/${kind}?width=${width}&token=${encodeURIComponent(this.assetToken)}`;
  }

  async getLibraryItems(libraryId: string, q: LibraryItemsQuery): Promise<LibraryPage> {
    await this.ensureAssetToken();
    const res = await this.conn.request<Envelope<ServerItem>>(
      `/v1/libraries/${libraryId}/items?${libraryItemsSearchParams(q)}`,
    );
    return { items: res.items.map((i) => this.toMediaItem(i)), total: res.meta.total };
  }

  async getLibraryLetters(libraryId: string, q: LibraryFilterQuery): Promise<LetterBucket[] | undefined> {
    try {
      const res = await this.conn.request<{ buckets: LetterBucket[] }>(
        `/v1/libraries/${libraryId}/items/letters?${libraryFilterSearchParams(q)}`,
      );
      return res.buckets;
    } catch (err) {
      if (isNotFound(err)) return undefined; // a server before the browser routes
      throw err;
    }
  }

  async getLibraryFacets(libraryId: string): Promise<LibraryFacets | undefined> {
    try {
      return await this.conn.request<LibraryFacets>(`/v1/libraries/${libraryId}/facets`);
    } catch (err) {
      if (isNotFound(err)) return undefined;
      throw err;
    }
  }

  async getItem(itemId: string): Promise<MediaItem | null> {
    await this.ensureAssetToken();
    try {
      const it = await this.conn.request<ServerItem>(`/v1/items/${itemId}`);
      return this.toMediaItem(it);
    } catch {
      return null;
    }
  }

  async getHomeHubs(): Promise<Hub[]> {
    await this.ensureAssetToken();
    const res = await this.conn.request<
      Envelope<{
        id: string;
        title: string;
        items: ServerItem[];
        libraryId?: string;
        libraryTitle?: string;
        libraryType?: "movie" | "show";
      }>
    >("/v1/hubs/home");
    return res.items
      .map((h) => ({
        id: h.id,
        title: h.title,
        items: h.items.map((i) => this.toMediaItem(i)),
        libraryId: h.libraryId,
        libraryTitle: h.libraryTitle,
        libraryType: h.libraryType,
      }))
      .filter((h) => h.items.length > 0);
  }

  async search(query: string): Promise<MediaItem[]> {
    const q = query.trim();
    if (!q) return [];
    await this.ensureAssetToken();
    const res = await this.conn.request<Envelope<ServerItem>>(
      `/v1/search?q=${encodeURIComponent(q)}&limit=50`,
    );
    return res.items.map((i) => this.toMediaItem(i));
  }

  /**
   * GET /v1/items/lookup — every copy of a canonical guid this account can
   * reach on this server, all media versions attached. No asset-token mint:
   * the "watch from" cards render no artwork, and this runs against every
   * online server on each Discovery detail view. A server predating the
   * route answers a 404 problem from its {itemID} handler → [] (feature-
   * detect, never version-sniff); other failures propagate so the caller's
   * per-server catch decides.
   */
  async lookupItems(guid: string, opts?: { season: number; episode: number }): Promise<MediaItem[]> {
    const q = new URLSearchParams({ guid });
    if (opts) {
      q.set("season", String(opts.season));
      q.set("episode", String(opts.episode));
    }
    try {
      const res = await this.conn.request<Envelope<ServerItem>>(`/v1/items/lookup?${q.toString()}`);
      return res.items.map((i) => this.toMediaItem(i));
    } catch (err) {
      if (err instanceof ApiProblemError && err.problem.status === 404) return [];
      throw err;
    }
  }

  /**
   * GET /v1/items/lookup?guids=a,b,c — every copy of up to 40 canonical
   * guids in one round trip, keyed by the guid each copy was asked under
   * (a cross-library duplicate's own guid is local://). Every asked guid is
   * a key, [] when the server holds nothing. A 404 is a server with no
   * lookup route at all → every key empty; a 400 validation_failed is a
   * server predating the batch form (it read an empty `guid`) → null, and
   * the caller falls back to lookupItems per guid. Anything else propagates.
   */
  async lookupItemsBatch(guids: string[]): Promise<Map<string, MediaItem[]> | null> {
    const out = new Map<string, MediaItem[]>(guids.map((g) => [g, []]));
    try {
      const res = await this.conn.request<Envelope<ServerItem>>(
        `/v1/items/lookup?guids=${guids.map(encodeURIComponent).join(",")}`,
      );
      for (const it of res.items) {
        out.get(it.lookupGuid ?? it.guid)?.push(this.toMediaItem(it));
      }
      return out;
    } catch (err) {
      if (err instanceof ApiProblemError && err.problem.status === 404) return out;
      if (
        err instanceof ApiProblemError &&
        err.problem.status === 400 &&
        err.problem.code === "validation_failed"
      ) {
        return null;
      }
      throw err;
    }
  }

  imageUrl(item: MediaItem, kind: "poster" | "backdrop" | "thumb", minWidth: number): string {
    const variants = kind === "thumb" ? (item.images.thumb ?? []) : item.images[kind];
    if (variants.length === 0) return "";
    const base = this.conn.baseUrlSync();
    if (!base || !this.assetTokenFresh()) {
      // Kick a mint (which also establishes the base); notifyAssets re-renders
      // subscribers, whose next imageUrl call returns the real URL. Meanwhile
      // serve the stale token if we have one — it may still be honored.
      void this.ensureAssetToken().catch(() => {});
      if (!base || !this.assetToken) return "";
    }
    // Small fixed width ladder per kind (smallest ≥ minWidth, else largest)
    // so the server's per-width resize cache stays hot.
    const ladder = kind === "poster" ? [342, 780] : kind === "backdrop" ? [1280, 1920] : [576];
    const width = ladder.find((w) => w >= minWidth) ?? ladder[ladder.length - 1]!;
    // updatedAt as a cache key: art replaced by a Fix Match or a refresh
    // would otherwise sit behind the browser's copy of the same URL.
    const v = item.updatedAt ? `&v=${encodeURIComponent(item.updatedAt)}` : "";
    return `${base}/v1/items/${item.id}/images/${kind}?width=${width}&token=${encodeURIComponent(this.assetToken)}${v}`;
  }

  /**
   * Poster URL straight from an item id — for surfaces like the dashboard
   * that have a hasPoster flag but no full MediaItem to hand imageUrl.
   */
  posterUrl(itemId: string, width = 342): string {
    const base = this.conn.baseUrlSync();
    if (!base || !this.assetTokenFresh()) {
      void this.ensureAssetToken().catch(() => {});
      if (!base || !this.assetToken) return "";
    }
    return `${base}/v1/items/${itemId}/images/poster?width=${width}&token=${encodeURIComponent(this.assetToken)}`;
  }

  /**
   * Chapters, skip markers and the scrubber preview index of one part
   * (GET /v1/parts/{id}/analysis). A server predating media analysis
   * answers a 404 problem → undefined (feature-detect, never version-sniff).
   */
  /** The item detail as the server sent it — Get Info's "View JSON". */
  getItemRaw(itemId: string): Promise<unknown> {
    return this.conn.request<unknown>(`/v1/items/${encodeURIComponent(itemId)}`);
  }

  async getPartAnalysis(partId: string): Promise<PartAnalysis | undefined> {
    // The strip and the menus need tile/still URLs right after: warm the
    // asset token alongside.
    await this.ensureAssetToken();
    try {
      const raw = await this.conn.request<Partial<PartAnalysis>>(
        `/v1/parts/${encodeURIComponent(partId)}/analysis`,
      );
      return normalizeAnalysis(raw);
    } catch (err) {
      if (err instanceof ApiProblemError && err.problem.status === 404) return undefined;
      throw err;
    }
  }

  /** One preview sheet (imageUrl idiom: "" while the base or token is cold). */
  previewTileUrl(partId: string, tile: number): string {
    return this.partAssetUrl(`/v1/parts/${encodeURIComponent(partId)}/previews/${tile}.jpg`, "");
  }

  /** One chapter still. The server serves the 480 px still as generated;
   * the width rides along for symmetry with item images. */
  chapterThumbUrl(partId: string, index: number, width: number): string {
    const w = [320, 480].find((x) => x >= width) ?? 480;
    return this.partAssetUrl(
      `/v1/parts/${encodeURIComponent(partId)}/chapters/${index}/thumb`,
      `width=${w}&`,
    );
  }

  private partAssetUrl(path: string, query: string): string {
    const base = this.conn.baseUrlSync();
    if (!base || !this.assetTokenFresh()) {
      void this.ensureAssetToken().catch(() => {});
      if (!base || !this.assetToken) return "";
    }
    return `${base}${path}?${query}token=${encodeURIComponent(this.assetToken)}`;
  }

  /** The active transcode session, if any (one per source at a time). */
  private activeSession: { itemId: string; sessionId: string } | null = null;

  async getPlayback(itemId: string, opts: PlaybackOptions = {}): Promise<PlaybackInfo> {
    // A prior session (quality switch, another item) must die first —
    // the server caps simultaneous transcodes.
    this.stopActiveSession();

    const constraints: Record<string, unknown> = {};
    if (opts.maxBitrateKbps) constraints.maxBitrateKbps = opts.maxBitrateKbps;
    if (opts.maxResolution) constraints.maxResolution = opts.maxResolution;
    if (opts.audioStreamIndex !== undefined) constraints.audioStreamIndex = opts.audioStreamIndex;
    if (opts.subtitleStreamIndex !== undefined) {
      constraints.subtitleStreamIndex = opts.subtitleStreamIndex;
    }
    if (opts.subtitleMode) constraints.subtitleMode = opts.subtitleMode;
    if (opts.directPlay !== undefined) constraints.directPlay = opts.directPlay;
    if (opts.directStream !== undefined) constraints.directStream = opts.directStream;
    if (opts.decodeFailed) constraints.decodeFailed = true;

    const decision = await this.conn.request<{
      decisionId: string;
      verdict: PlaybackVerdict;
      directUrl: string;
      hls: { startUrl: string } | null;
      playbackToken: { token: string; expiresAt: string };
      subtitles?: Array<{
        index: number;
        lang?: string;
        label?: string;
        codec?: string;
        forced?: boolean;
        hearingImpaired?: boolean;
        external?: boolean;
        source?: string;
        url: string;
      }>;
      streamSubtitles?: number[];
      target?: TranscodeTarget | null;
      limit?: PlaybackLimit | null;
    }>("/v1/playback/decisions", {
      body: {
        itemId,
        protocol: "hls",
        profile: await getCapabilityProfile(),
        constraints,
      },
    });
    const base = this.conn.baseUrlSync();

    let hlsMasterUrl: string | null = null;
    let sessionId: string | undefined;
    let startedAtMs: number | undefined;
    if (decision.hls) {
      const session = await this.conn.request<{
        sessionId: string;
        masterUrl: string;
        startMs?: number;
      }>("/v1/playback/sessions", {
        body: {
          decisionId: decision.decisionId,
          ...(opts.startMs && opts.startMs > 0 ? { startMs: opts.startMs } : {}),
        },
      });
      sessionId = session.sessionId;
      this.activeSession = { itemId, sessionId: session.sessionId };
      hlsMasterUrl = `${base}${session.masterUrl}`;
      startedAtMs = session.startMs;
    }

    const item = await this.getItem(itemId);
    return {
      hlsMasterUrl,
      directUrl: `${base}${decision.directUrl}`,
      durationMs: item?.runtimeMs ?? 0,
      subtitles: (decision.subtitles ?? []).map((t) => ({
        lang: t.lang || "und",
        label: t.label || t.lang || "Subtitles",
        url: `${base}${t.url}`,
        streamIndex: t.index,
        hearingImpaired: t.hearingImpaired ?? false,
        external: t.external ?? false,
        source: t.source || undefined,
        codec: t.codec || undefined,
        forced: t.forced ?? false,
      })),
      streamSubtitles: decision.streamSubtitles ?? [],
      verdict: decision.verdict,
      decisionId: decision.decisionId,
      // The decision names the exact part its directUrl serves — parse it
      // (pre-prefix, server-relative) rather than trusting parts[0].
      partId: partIdFromPath(decision.directUrl) ?? undefined,
      sessionId,
      startedAtMs,
      target: decision.target ?? null,
      limit: decision.limit ?? null,
    };
  }

  /**
   * DELETE the transcode session. Keepalive fetch with an explicit Bearer
   * (sendBeacon can't set headers) so it survives pagehide; the server's
   * idle reaper is the backstop when even this is lost.
   */
  stopPlayback(info: PlaybackInfo): void {
    if (!info.sessionId) return;
    if (this.activeSession?.sessionId === info.sessionId) this.activeSession = null;
    this.deleteSession(info.sessionId);
  }

  private stopActiveSession(): void {
    const active = this.activeSession;
    this.activeSession = null;
    if (active) this.deleteSession(active.sessionId);
  }

  private deleteSession(sessionId: string): void {
    const base = this.conn.baseUrlSync();
    const token = this.conn.currentToken();
    if (!base || !token) return;
    void fetch(`${base}/v1/playback/sessions/${sessionId}`, {
      method: "DELETE",
      headers: { authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => {});
  }

  getUserState(itemId: string): UserItemState {
    return this.watch.get(itemId);
  }

  reportProgress(
    itemId: string,
    state: "playing" | "paused" | "stopped" | "ended",
    timeMs: number,
    durationMs: number,
    sessionId?: string,
    extras?: TimelineExtras,
    opts?: { keepalive?: boolean },
  ): Promise<void> {
    this.watch.report(itemId, state, timeMs, durationMs);
    const body = {
      itemId,
      state,
      timeMs,
      durationMs,
      ...(sessionId ? { sessionId } : {}),
      ...extras,
    };
    if (opts?.keepalive) {
      // The deleteSession pattern: synchronous base+token and a keepalive
      // fetch with an explicit Bearer (sendBeacon can't set headers), so a
      // final beat survives pagehide and unmount. Mid-session the token is
      // fresh — the 10 s cadence rides conn.request's re-mint ladder.
      const base = this.conn.baseUrlSync();
      const token = this.conn.currentToken();
      if (base && token) {
        return fetch(`${base}/v1/timeline`, {
          method: "POST",
          headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
          body: JSON.stringify(body),
          keepalive: true,
        }).then(
          () => undefined,
          () => undefined,
        );
      }
    }
    return this.conn.request(`/v1/timeline`, { body }).then(
      () => undefined,
      () => undefined,
    );
  }

  setWatched(itemId: string, watched: boolean): void {
    this.watch.setWatched(itemId, watched);
    void this.conn
      .request(`/v1/items/${itemId}/${watched ? "scrobble" : "unscrobble"}`, { method: "POST" })
      .catch(() => {});
  }

  setStreamSelection(
    itemId: string,
    sel: { audioStreamIndex?: number | null; subtitleStreamIndex?: number | null },
  ): void {
    this.watch.setStreamSelection(itemId, sel);
    void this.conn
      .request(`/v1/items/${itemId}/streams`, { method: "PUT", body: sel })
      .catch(() => {})
      .finally(() => this.watch.clearSelectionDirty(itemId));
  }
}
